/**
 * The app-to-API path (spec 9.6) for the few screens that talk to the server directly:
 * verification first, reports and admin later. Every request carries the client version;
 * unless told otherwise it also carries the session's bearer token from the same provider
 * the sync loop uses (section 05 registers it with `setApiTokenProvider`). The wrapper
 * refuses to start offline and turns every failure into an `ApiClientError` whose `kind`
 * the screen can word: offline, rate_limited, not_found and so on. A 403 refusing the
 * session for an account step (plan 5) also tells the listener section 05 registers
 * (`setApiLangkahListener`), which opens the step's screen; the request still throws.
 */
import type { TokenProvider } from '../sync/tokenBroker'

export const API_BASE = '/api'
/** Mirrors CLIENT_VERSION_HEADER in @satu/shared/sync/wire, kept literal so the public page stays small. */
export const CLIENT_VERSION_HEADER = 'X-SATU-Client'
/** Mirrors CLIENT_VERSION_HEADER_LAMA: legacy wire name from SIMU, sent beside the new one. */
export const CLIENT_VERSION_HEADER_LAMA = 'X-SIMU-Client'
/** The build sets VITE_APP_VERSION (section 22); local builds fall back to 0.1.0. */
export const CLIENT_VERSION: string = import.meta.env.VITE_APP_VERSION ?? '0.1.0'

export type ApiFailure =
  | 'offline'
  | 'invalid'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'rate_limited'
  | 'server'

export interface ApiClientErrorInit {
  status?: number
  code?: string
  message?: string
  detail?: unknown
  cause?: unknown
}

export class ApiClientError extends Error {
  readonly kind: ApiFailure
  readonly status: number | null
  /** The server's error code (`rate_limited`, `not_found`, ...) when it sent one. */
  readonly code: string | null
  readonly detail: unknown

  constructor(kind: ApiFailure, init: ApiClientErrorInit = {}) {
    super(init.message ?? kind, init.cause === undefined ? undefined : { cause: init.cause })
    this.name = 'ApiClientError'
    this.kind = kind
    this.status = init.status ?? null
    this.code = init.code ?? null
    this.detail = init.detail
  }
}

export function failureOf(status: number): ApiFailure {
  switch (status) {
    case 400:
      return 'invalid'
    case 401:
      return 'unauthorized'
    case 403:
      return 'forbidden'
    case 404:
      return 'not_found'
    case 429:
      return 'rate_limited'
    default:
      return 'server'
  }
}

/** Akun & keamanan (spec 2026-09-30, 6): the two refusals that open a screen, not an error. */
export type LangkahApi = 'ganti_sandi' | 'dua_langkah'

/**
 * The step a 403 names, or null. Literals, not `@satu/shared`: this module also serves the
 * small public page.
 */
function langkahDari(status: number, code: string | undefined): LangkahApi | null {
  if (status !== 403) return null
  if (code === 'harus_ganti_sandi') return 'ganti_sandi'
  if (code === 'perlu_verifikasi_dua_langkah') return 'dua_langkah'
  return null
}

export type QueryValue = string | number | null | undefined

export interface ApiRequest {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  /** Empty, null and undefined values are left out of the query string. */
  query?: Record<string, QueryValue>
  body?: unknown
  /** Attach the session token (default). The public page passes false. */
  auth?: boolean
  signal?: AbortSignal
}

export interface ApiClientOptions {
  baseUrl?: string
  fetch?: typeof fetch
  isOnline?: () => boolean
  clientVersion?: string
  tokenProvider?: TokenProvider | null
  /** Told when the API refuses a request for an account step (the request still throws). */
  onLangkah?: ((langkah: LangkahApi) => void) | null
}

export interface ApiClient {
  fetch<T>(path: string, request?: ApiRequest): Promise<T>
  setTokenProvider(provider: TokenProvider | null): void
  setLangkahListener(fn: ((langkah: LangkahApi) => void) | null): void
}

export function queryString(query: Record<string, QueryValue> | undefined): string {
  if (query === undefined) return ''
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    params.set(key, String(value))
  }
  const text = params.toString()
  return text.length === 0 ? '' : `?${text}`
}

async function readJson(response: Response): Promise<Record<string, unknown> | null> {
  const type = response.headers.get('content-type') ?? ''
  if (!type.includes('json')) return null
  try {
    const value: unknown = await response.json()
    return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null
  } catch {
    return null
  }
}

/** The browser's link signal; true where there is no navigator (tests, workers). */
function browserOnline(): boolean {
  const nav = (globalThis as { navigator?: { onLine?: boolean } }).navigator
  return nav?.onLine !== false
}

function isAbort(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError'
}

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const base = options.baseUrl ?? API_BASE
  const fetchFn = options.fetch ?? ((input, init) => fetch(input, init))
  const isOnline = options.isOnline ?? browserOnline
  const version = options.clientVersion ?? CLIENT_VERSION
  let tokenProvider: TokenProvider | null = options.tokenProvider ?? null
  let langkahListener = options.onLangkah ?? null

  return {
    async fetch<T>(path: string, request: ApiRequest = {}): Promise<T> {
      if (!isOnline()) throw new ApiClientError('offline')
      const headers: Record<string, string> = {
        Accept: 'application/json',
        [CLIENT_VERSION_HEADER]: version,
        [CLIENT_VERSION_HEADER_LAMA]: version,
      }
      if (request.auth !== false && tokenProvider !== null) {
        let token: string | null = null
        try {
          token = await tokenProvider()
        } catch {
          token = null
        }
        if (token !== null) headers.Authorization = `Bearer ${token}`
      }
      const init: RequestInit = { method: request.method ?? 'GET', headers }
      if (request.signal !== undefined) init.signal = request.signal
      if (request.body !== undefined) {
        headers['Content-Type'] = 'application/json'
        init.body = JSON.stringify(request.body)
      }
      let response: Response
      try {
        response = await fetchFn(`${base}${path}${queryString(request.query)}`, init)
      } catch (error) {
        if (isAbort(error)) throw error
        // A failed fetch is the network: DNS, a dropped link, a captive portal.
        throw new ApiClientError('offline', { cause: error })
      }
      const payload = await readJson(response)
      if (!response.ok) {
        const init: ApiClientErrorInit = { status: response.status }
        if (typeof payload?.error === 'string') init.code = payload.error
        if (typeof payload?.message === 'string') init.message = payload.message
        if (payload?.detail !== undefined) init.detail = payload.detail
        const langkah = langkahDari(response.status, init.code)
        if (langkah !== null) {
          try {
            langkahListener?.(langkah)
          } catch {
            // The screen still gets its error; the step opens with the next refusal.
          }
        }
        throw new ApiClientError(failureOf(response.status), init)
      }
      return payload as T
    },
    setTokenProvider(provider) {
      tokenProvider = provider
    },
    setLangkahListener(fn) {
      langkahListener = fn
    },
  }
}

/** The one client of the app; screens call `apiFetch`, section 05 sets the token provider. */
export const api: ApiClient = createApiClient()

export function apiFetch<T>(path: string, request?: ApiRequest): Promise<T> {
  return api.fetch<T>(path, request)
}

export function setApiTokenProvider(provider: TokenProvider | null): void {
  api.setTokenProvider(provider)
}

export function setApiLangkahListener(fn: ((langkah: LangkahApi) => void) | null): void {
  api.setLangkahListener(fn)
}
